import React, { useState, useContext, useEffect } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import Field from "../fields/Field";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import QuizExampleDesktop from "/app/assets/images/elements/quiz_example_desktop.svg";
import QuizExampleMobile from "/app/assets/images/elements/quiz_example_mobile.svg";
import arePropsEqual from "../../utils/arePropsEqual";
import QuizConditionalContentService from "../../packs/services/convertflow/elements/quiz_conditional_content_service";
import VideoService from "../../packs/services/convertflow/elements/video_service";
import QuizOptionHeightService from "../../packs/services/convertflow/elements/quiz_option_height_service";
import {
  resolveQuizClasses,
  buildQuizStylesheet,
  quizHasDefinedHeight,
} from "./ElementQuizStyleResolver";
import BuilderStyleTag from "../shared/BuilderStyleTag";
import { scopeCustomCode } from "../../utils/builderUtils";

const ElementQuiz = (props) => {
  let element = props.element;
  let sorted_fields = [];
  let sorted_fields_without_subfields = Object.values(element.fields)
    .filter(
      (field) =>
        field.element_id == element.id &&
        field.toBeDeleted !== true &&
        !field.field_id
    )
    .sort((a, b) => a.position - b.position);

  Object.values(sorted_fields_without_subfields).forEach((field) => {
    sorted_fields.push(field);

    Object.values(element.fields)
      .filter(
        (sub_field) =>
          sub_field.element_id == element.id &&
          sub_field.toBeDeleted !== true &&
          sub_field.field_id == field.id
      )
      .sort((a, b) => a.position - b.position)
      .forEach((sub_field) => {
        sorted_fields.push(sub_field);
      });
  });

  const classes = resolveQuizClasses(element, props.builder, props.device);
  const stylesheet = buildQuizStylesheet(
    props.builder,
    props.device,
    props.cta,
    element,
    props.variant,
    sorted_fields,
    props.section
  );

  const [currentQuestion, setCurrentQuestion] = useState(sorted_fields[0]);

  if (props.builder == true) {
    const [contextData, builder, setContextData] = useContext(
      VariantContextData
    );

    const updateQuestion = (question) => {
      if (!question) {
        return;
      }

      setCurrentQuestion(question);

      if (window.selected_object_tab == "Content") {
        dispatchCustomEvent("focusItem", {
          parent_id: element.id,
          item_type: "fields",
          item_id: question.id
        });
      }
    };

    useEffect(() => {
      const queryParameters = new URLSearchParams(window.location.search);
      const test_last_field = queryParameters.get("test_last_field");

      const previewConditionalContent = (event) => {
        const option = event.currentTarget;
        const fieldId = option.getAttribute("data-field-id");
        const fieldOptionId = option.getAttribute("data-field-option-id");
        if (!fieldId || !fieldOptionId) {
          return;
        }

        const container = document.querySelector(
          `#builder .cf-quiz-question[data-field-id="${fieldId}"] .cf-quiz-conditional-content`
        );
        if (!container) {
          return;
        }

        QuizConditionalContentService.toggle(container, fieldOptionId);
      };

      if (window.selected_object_id == element.id) {
        updateQuestion(
          test_last_field == "true"
            ? sorted_fields[sorted_fields.length - 1]
            : sorted_fields[0]
        );

        document
          .querySelectorAll(`#builder .cf-quiz-question-option`)
          .forEach((option) => {
            option.addEventListener("dblclick", () => openAnswerModal(option));
            option.addEventListener("click", previewConditionalContent);
          });
      }
      return function cleanup() {
        document
          .querySelectorAll(`#builder .cf-quiz-question-option`)
          .forEach((input) => {
            input.removeEventListener("dblclick", openAnswerModal);
            input.removeEventListener("click", previewConditionalContent);
          });
      };
    }, [window.selected_object_id]);

    useEffect(() => {
      if (
        !currentQuestion ||
        !sorted_fields.find((f) => f.id == currentQuestion?.id)
      ) {
        updateQuestion(sorted_fields[0]);
      }
    }, [sorted_fields]);

    useEffect(() => {
      if (contextData.selected_item_parent_id == element.id) {
        let target_field = sorted_fields.find((field) => field.id == contextData.selected_item_id);

        if (target_field && target_field.id !== currentQuestion.id) {
          updateQuestion(target_field);
        }
      }
    }, [contextData.selected_item_id]);
  }

  const optionContentKey = Object.values(currentQuestion?.field_options || {})
    .map((option) => `${option.id}:${option.label}:${option.image}`)
    .join("|");

  useEffect(() => {
    if (!currentQuestion) {
      return;
    }

    document
      .querySelectorAll(
        `.cf-element[data-element-id="${element.id}"] .cf-quiz-question[data-field-id="${currentQuestion.id}"]`
      )
      .forEach((question) => {
        QuizOptionHeightService.apply(
          question,
          element,
          props.variant,
          currentQuestion
        );
      });
  }, [
    currentQuestion?.id,
    stylesheet,
    optionContentKey,
    QuizOptionHeightService.resolvedValue(element, props.variant, currentQuestion),
  ]);

  const openAnswerModal = (option) => {
    let field_id = option.getAttribute("data-field-id");
    let field_option_id = option.getAttribute("data-field-option-id");

    let edit_button_question = document.querySelector(
      `.cf-item-box[data-object-id="${field_id}"] svg[data-icon="pen-to-square"]`
    );
    if (edit_button_question) {
      edit_button_question.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    }

    if (field_option_id) {
      let edit_button_answer = document.querySelector(
        `.cf-item-box[data-object-id="${field_option_id}"] svg[data-icon="pen-to-square"]`
      );

      if (edit_button_answer) {
        edit_button_answer.dispatchEvent(
          new MouseEvent("click", { bubbles: true })
        );
      }
    }
  };

  const currentQuestionIndex = () => {
    return sorted_fields.findIndex((f) => f.id == currentQuestion?.id);
  };

  const canHaveNextButton = () => {
    let has_next_field = currentQuestionIndex() + 1 <= sorted_fields.length;
    let not_finished = currentQuestionIndex() !== -1;
    let question;

    if (currentQuestion) {
      question = sorted_fields.find((e) => e.id == currentQuestion.id);
    }

    let currentCheck =
      question &&
      (question.multi_select == true || question.field_type == "range");

    let showing_loader =
      question &&
      question.options["show-loading-spinner"] == "true" &&
      question.field_type == "empty";

    const hide_next_button = question && question.options["hide-next-button"]
      ? question.options["hide-next-button"]
      : element.options["hide-next-button"]
        ? element.options["hide-next-button"]
        : "false";

    if (hide_next_button == "true" || showing_loader) {
      return false;
    }

    return (
      ((has_next_field && not_finished) || currentCheck) && !showing_loader
    );
  };

  const canHaveBackButton = () => {
    let has_previous_field = currentQuestionIndex() > 0;
    let is_finished = currentQuestionIndex() == -1 && props.builder == true;
    let question;

    if (currentQuestion) {
      question = sorted_fields.find((e) => e.id == currentQuestion.id);
    }

    let not_hidden = question && question.options["hide-back-button"]
      ? question.options["hide-back-button"] !== "true"
      : element.options["hide-back-button"]
        ? element.options["hide-back-button"] !== "true"
        : "true";


    let showing_loader =
      question &&
      question.options["show-loading-spinner"] == "true" &&
      question.field_type == "empty";

    return (has_previous_field || is_finished) && not_hidden && !showing_loader;
  };

  const quizProgressInnerWidth = (position) => {
    const parsedMax = parseInt(element.options["quiz-progress-max-percentage"]);
    const max = Number.isNaN(parsedMax) ? 100 : parsedMax;
    const min = Math.min(
      parseInt(element.options["quiz-progress-min-percentage"]) || 0,
      max
    );
    return (
      min + ((max - min) * position) / sorted_fields_without_subfields.length
    );
  };

  const QuizProgressMessage = (props) => {
    const element = props.element;

    return (
      <React.Fragment>
        <div
          className="cf-progress-message"
          data-font={element.options["quiz-progress-text-font"]}
        >
          {element.options["quiz-progress-label"]}{" "}
          <span className="cf-progress-step-progress">
            {props.position}/{sorted_fields_without_subfields.length}
          </span>
        </div>
      </React.Fragment>
    );
  };

  const quizFormClasses = (field_item) => {
    let classes = "";

    if (
      field_item.options["quiz-answer-width"] &&
      field_item.options["quiz-answer-width"] !== "custom"
    ) {
      classes += ` ${field_item.options["quiz-answer-width"]}`;
    } else if (
      element.options["quiz-answer-width"] &&
      element.options["quiz-answer-width"] !== "custom" &&
      field_item.options["quiz-answer-width"] !== "custom"
    ) {
      classes += ` ${element.options["quiz-answer-width"]}`;
    }

    if (
      field_item.options["quiz-answer-field-size"] &&
      field_item.options["quiz-answer-field-size"] !== "custom"
    ) {
      classes += ` ${field_item.options["quiz-answer-field-size"]}`;
    } else if (
      field_item.options["quiz-answer-field-size"] &&
      field_item.options["quiz-answer-field-size"] == "custom"
    ) {
      classes += ' cf-field-custom';
    } else if (
      element.options["quiz-answer-field-size"] &&
      element.options["quiz-answer-field-size"] !== "custom"
    ) {
      classes += ` ${element.options["quiz-answer-field-size"]}`;
    } else if (
      element.options["quiz-answer-field-size"] &&
      element.options["quiz-answer-field-size"] == "custom"
    ) {
      classes += ' cf-field-custom';
    } else {
      classes += ' cf-field-regular';
    }

    classes +=
      " " +
      (field_item.options["quiz-answer-field-rounded"]
        ? field_item.options["quiz-answer-field-rounded"]
        : element.options["quiz-answer-field-rounded"]
          ? element.options["quiz-answer-field-rounded"]
          : "cf-field-slightly-rounded");

    return classes;
  };

  const quizButtonClasses = () => {
    let classes = "element-content cf-button cf-button-primary cf-text-element";

    classes +=
      " " +
      (element.options["quiz-button-size"]
        ? element.options["quiz-button-size"]
        : "cf-button-small");

    return classes;
  };

  const resolveStickyBool = (key) => {
    const elVal = element.options[key];
    const val =
      elVal !== undefined && elVal !== null && elVal !== ""
        ? elVal
        : props.variant.options[key] || "";
    return val === "true";
  };

  const quizProgressSticky = resolveStickyBool("quiz-progress-bar-sticky");
  const forceStickyForHeight = quizHasDefinedHeight(element.options);
  const quizNextSticky =
    forceStickyForHeight || resolveStickyBool("quiz-next-button-sticky");
  const quizBackSticky =
    forceStickyForHeight || resolveStickyBool("quiz-back-button-sticky");
  const quizProgressInline = !quizProgressSticky;
  const quizNextInline = !quizNextSticky;
  const quizBackInline = !quizBackSticky;
  const quizHasTopStickyItems = quizProgressSticky;
  const quizHasBottomStickyItems = quizNextSticky || quizBackSticky;

  const quizElementDataMultiSelect = (field_item) => {
    return field_item.field_type == "radio" ? "false"
      : (field_item.multi_select == true || field_item.field_type == "checkbox")
        ? "true" : "false";
  };

  return (
    <React.Fragment>
      <div className="element-content">
        <div
          className={classes}
          id={`cf-quiz-${element.id}`}
          data-font={
            element.options["collapse-heading-font-family"] ||
            props.variant.options["collapse-heading-font-family"]
          }
        >
          <input type="hidden" id={`cf-quiz-${element.id}-history`} />
          <input type="hidden" id={`cf-quiz-${element.id}-answers`} />
          <input
            type="hidden"
            id={`cf-quiz-${element.id}-current-question`}
            value={JSON.stringify(currentQuestion)}
            readOnly={true}
          />
          {sorted_fields.map((field_item, index) => {
            const sub_field_main_question_position =
              sorted_fields_without_subfields.findIndex(
                (f) => f.id == field_item.field_id
              ) + 1;
            const field_question_position =
              sorted_fields_without_subfields.findIndex(
                (f) => f.id == field_item.id
              ) + 1;

            const field_item_position = field_item.field_id
              ? sub_field_main_question_position
              : field_question_position;

            const labelImage = field_item.options["label-image"];
            const labelImagePlacement =
              props.device == "desktop"
                ? field_item.options["label-image-placement"] ||
                  props.variant.options["label-image-placement"]
                : field_item.options["label-image-placement-mobile"] ||
                  props.variant.options["label-image-placement-mobile"];

            const conditionalContentOptions = Object.values(
              field_item.field_options || {}
            )
              .filter(
                (field_option) =>
                  field_option.toBeDeleted !== true &&
                  field_option.options &&
                  field_option.options["conditional-content"]
              )
              .sort((a, b) => a.position - b.position);

            const conditionalContentPlacement =
              element.options["quiz-conditional-content-placement"] ||
              props.variant.options["quiz-conditional-content-placement"] ||
              "above";

            const renderConditionalContent = () => {
              if (conditionalContentOptions.length === 0) {
                return null;
              }

              return (
                <div
                  className="cf-quiz-conditional-content"
                  data-field-id={field_item.id}
                  data-placement={conditionalContentPlacement}
                  style={{
                    display: props.builder == true ? "block" : "none",
                  }}
                >
                  {conditionalContentOptions.map((option, optionIndex) => {
                    const html = option.options["conditional-content"];
                    const isDefaultPreview =
                      props.builder == true && optionIndex === 0;

                    return (
                      <div
                        key={`quiz-conditional-content-${option.id}`}
                        className="cf-quiz-conditional-content-item cf-merge-tag-allowed"
                        data-field-option-id={option.id}
                        style={{
                          display: isDefaultPreview ? "block" : "none",
                        }}
                        dangerouslySetInnerHTML={{
                          __html:
                            props.builder == true
                              ? scopeCustomCode(html, `#cta_${props.cta.id}`)
                              : html,
                        }}
                      />
                    );
                  })}
                </div>
              );
            };

            const progressJSX = element.options["show-progress-bar"] !== "false" && (
              <div className="cf-progress">
                {element.options["quiz-progress-label-format"] == "line" && (
                  <QuizProgressMessage element={element} position={field_item_position} />
                )}
                <div className="cf-progress-outer">
                  <div
                    className="cf-progress-inner"
                    data-progress-format={element.options["quiz-progress-label-format"]}
                    style={{ width: `${quizProgressInnerWidth(field_item_position)}%` }}
                  >
                    {element.options["quiz-progress-label-format"] == "box" && (
                      <QuizProgressMessage element={element} position={field_item_position} />
                    )}
                  </div>
                </div>
              </div>
            );

            const backButtonJSX = (
              <div
                id={`cf-back-button-quiz-${element.id}`}
                className={quizButtonClasses() + " cf-quiz-back-button"}
                style={{ display: canHaveBackButton() ? "block" : "none" }}
                data-field-id={field_item.id}
                role="button"
                tabIndex="0"
                aria-label={"Previous Question"}
              >
                <p className="cf-label">
                  {field_item.options["back-button-label"] || element.options["back-button-label"] || "← Back"}
                </p>
              </div>
            );

            const nextButtonJSX = (
              <div
                id={`cf-next-button-quiz-${element.id}`}
                className={quizButtonClasses() + " cf-quiz-next-button"}
                style={{ display: canHaveNextButton() ? "block" : "none" }}
                data-field-id={field_item.id}
                role="button"
                tabIndex="0"
                aria-label={"Next Question"}
              >
                <p className="cf-label">
                  {field_item.options["next-button-label"] || element.options["next-button-label"] || "Next →"}
                </p>
              </div>
            );

            return (
              <div
                className="cf-quiz-question"
                data-multi-select={quizElementDataMultiSelect(field_item)}
                data-multi-select-limit={
                  field_item.options["multi-select-limit"]
                }
                data-required={field_item.required}
                data-field-id={field_item.id}
                data-field-slug={field_item.slug}
                data-field-type={field_item.field_type}
                data-index={index}
                key={`quiz_question-${field_item.id}`}
                style={{
                  display:
                    currentQuestion?.id == field_item.id ? "block" : "none",
                }}
              >
                <div className="cf-quiz-answers-outer-wrapper">
                  {labelImage && (
                    <img
                      className="cf-quiz-label-image cf-quiz-image-left-right-above"
                      src={labelImage}
                      style={{
                        display:
                          labelImage &&
                          ["left", "right", "above"].includes(
                            labelImagePlacement
                          )
                            ? "block"
                            : "none",
                      }}
                    />
                  )}

                  <div className="cf-quiz-inner-wrapper">
                    {quizHasTopStickyItems && (
                      <div className="cf-quiz-sticky-bar cf-quiz-sticky-top">
                        {quizProgressSticky && progressJSX}
                      </div>
                    )}

                    {quizProgressInline && progressJSX}

                    <div className="cf-quiz-content-answers-wrapper" data-field-id={field_item.id}>
                      <div style={{ width: '100%'}}>
                        <div
                          className="cf-quiz-question-label element-content cf-merge-tag-allowed"
                          dangerouslySetInnerHTML={{
                            __html:
                              props.builder == true
                                ? scopeCustomCode(
                                    field_item.label,
                                    `#cta_${props.cta.id}`
                                  )
                                : field_item.label,
                          }}
                        />
                        <div
                          className="cf-quiz-question-description cf-merge-tag-allowed"
                          dangerouslySetInnerHTML={{
                            __html:
                              props.builder == true
                                ? scopeCustomCode(
                                    field_item.options["description"],
                                    `#cta_${props.cta.id}`
                                  )
                                : field_item.options["description"],
                          }}
                        />
                      </div>

                      {field_item.options["show-loading-spinner"] == "true" &&
                        field_item.field_type == "empty" &&
                        !field_item.options["loading-style"] && (
                          <span className="cf-loading-spinner"></span>
                        )}

                      {field_item.options["show-loading-spinner"] == "true" &&
                        field_item.field_type == "empty" &&
                        field_item.options["loading-style"] == "bar" && (
                          <span className="cf-loading-bar"></span>
                        )}

                      {field_item.field_type == "video" &&
                        (() => {
                          const videoURL =
                            field_item.options["video-embed-url"] || "";
                          const videoURLFormatted = VideoService.formatEmbedUrl({
                            ...field_item.options,
                            enableEndedEvents:
                              (field_item.options["auto-continue-trigger"] ||
                                "done") === "done",
                          });
                          const videoOptions =
                            VideoService.iframeAllow(field_item.options);
                          const heightValue =
                            field_item.options["video-embed-height-value"];
                          const heightValueMobile =
                            field_item.options[
                              "video-embed-height-value-mobile"
                            ];

                          if (!videoURLFormatted && props.builder == true) {
                            return (
                              <div className="cf-html-placeholder element-content">
                                <strong>
                                  Video:{" "}
                                  <em>
                                    {videoURL ? videoURL : "Video Embed"}
                                  </em>
                                </strong>
                              </div>
                            );
                          }

                          if (!videoURLFormatted) return null;

                          return (
                            <React.Fragment>
                              <div
                                className="cf-video cf-quiz-video"
                                style={{
                                  width: "100%",
                                  ...(heightValue
                                    ? { height: `${heightValue}px` }
                                    : { paddingTop: "56.25%" }),
                                }}
                              >
                                <iframe
                                  allowFullScreen="1"
                                  {...(props.builder == true &&
                                  currentQuestion?.id == field_item.id
                                    ? { src: videoURLFormatted }
                                    : { "data-src": videoURLFormatted })}
                                  allow={videoOptions}
                                  loading="lazy"
                                  referrerPolicy={
                                    VideoService.detectProvider(videoURL) ===
                                    "youtube"
                                      ? "strict-origin-when-cross-origin"
                                      : undefined
                                  }
                                  scrolling="no"
                                ></iframe>
                              </div>
                              {heightValueMobile && (
                                <style
                                  dangerouslySetInnerHTML={{
                                    __html: `
@media (max-width: 599px) {
  #cta_${props.cta.id} .cf-quiz-question[data-field-id="${field_item.id}"] .cf-quiz-video {
    height: ${heightValueMobile}px !important;
    padding-top: 0 !important;
  }
}
`,
                                  }}
                                />
                              )}
                            </React.Fragment>
                          );
                        })()}

                      {labelImage && (
                        <img
                          className="cf-quiz-label-image cf-quiz-image-below"
                          src={labelImage}
                          style={{
                            display:
                              labelImage &&
                              !["left", "right", "above"].includes(
                                labelImagePlacement
                              )
                                ? "inline-block"
                                : "none",
                          }}
                        />
                      )}

                      {conditionalContentPlacement !== "below" &&
                        renderConditionalContent()}

                      <div className="cf-quiz-answers-wrapper">
                        {[
                          "text",
                          "dropdown",
                          "paragraph",
                          "radio",
                          "checkbox",
                          "number",
                          "date",
                          "range",
                        ].includes(field_item.field_type) && (
                          <div
                            className="cf-form-fields cf-quiz-question-option"
                            data-field-type={field_item.field_type}
                            data-field-id={field_item.id}
                            data-field-slug={field_item.slug}
                          >
                            <div
                              data-font={
                                field_item.options["quiz-answer-field-font"] || element.options["quiz-answer-field-font"]
                              }
                              className={`cf-form-2 cf-form-theme-default ${quizFormClasses(
                                field_item
                              )}`}
                            >
                              <form>
                                <Field
                                  key={"field-" + field_item.id}
                                  field={field_item}
                                  hide_label={true}
                                  {...props}
                                />
                              </form>
                            </div>
                          </div>
                        )}

                        {[
                          "survey",
                          "radio_survey",
                          "image",
                          "color",
                          "emoji",
                          "icon",
                          "letter",
                        ].includes(field_item.field_type) &&
                          Object.values(field_item.field_options)
                            .filter(
                              (field_option) => field_option.toBeDeleted !== true
                            )
                            .sort((a, b) => a.position - b.position)
                            .map((option, i) => {
                              return (
                                <div
                                  key={`quiz_question_answer-${option.id}`}
                                  className="cf-quiz-question-option"
                                  data-field-type={field_item.field_type}
                                  data-field-id={field_item.id}
                                  data-field-slug={field_item.slug}
                                  data-field-option-id={option.id}
                                  data-field-value={option.value || option.label}
                                  data-position={option.position}
                                  role="button"
                                  tabIndex="0"
                                  aria-label={option.value || option.label}
                                >
                                  {field_item.field_type === "image" && (
                                    <div className="cf-quiz-image-box">
                                      {option.image && option.image !== "none" ? (
                                        <img
                                          className="cf-quiz-option-image"
                                          src={option.image}
                                        ></img>
                                      ) : (
                                        <svg
                                          className="cf-quiz-option-image-svg cf-outer-center"
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 525.5 525.5"
                                        >
                                          <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path>
                                          <path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 33.9-10.9 68.9-13.8v13.8h-68.9zm68.9-72.7v46.8l-26.2-26.2c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-26.3 26.3-.9-.9c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-18.8 18.8V225h101.4z"></path>
                                          <path d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6s-2.9 6.6-6.6 6.6-6.6-2.9-6.6-6.6 3-6.6 6.6-6.6z"></path>
                                        </svg>
                                      )}
                                    </div>
                                  )}

                                  {["color", "emoji", "icon", "letter"].includes(
                                    field_item.field_type
                                  ) && (
                                    <div
                                      className="cf-quiz-visual-box"
                                      data-visual-type={field_item.field_type}
                                    >
                                      {field_item.field_type === "color" && (
                                        <div
                                          className="cf-quiz-option-color"
                                          style={{
                                            backgroundColor: option.color,
                                            color: option.color,
                                          }}
                                        >
                                          .
                                        </div>
                                      )}

                                      {field_item.field_type === "emoji" && (
                                        <div className="cf-quiz-option-emoji">
                                          {option.emoji || "😎"}
                                        </div>
                                      )}

                                      {field_item.field_type === "icon" && (
                                        <div className="cf-quiz-option-icon">
                                          <FontAwesomeIcon
                                            icon={option.icon || "fas fa-archive"}
                                          />
                                        </div>
                                      )}

                                      {field_item.field_type === "letter" &&
                                        option.letter && (
                                          <span className="cf-quiz-option-letter">
                                            {option.letter}
                                          </span>
                                        )}
                                    </div>
                                  )}

                                  {field_item.field_type === "radio_survey" && (
                                    <span className="cf-quiz-option-radio"></span>
                                  )}

                                  {option.label && (
                                    <div
                                      className="cf-quiz-option-label cf-merge-tag-allowed"
                                      dangerouslySetInnerHTML={{
                                        __html: option.label,
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })}
                      </div>

                      {conditionalContentPlacement === "below" &&
                        renderConditionalContent()}
                    </div>

                    {(quizBackInline || quizNextInline) && (
                      <div
                        className="cf-quiz-buttons-wrapper"
                        data-font={element.options["quiz-button-font"]}
                      >
                        {quizBackInline && backButtonJSX}
                        {quizNextInline && nextButtonJSX}
                      </div>
                    )}

                    {quizHasBottomStickyItems && (
                      <div
                        className="cf-quiz-sticky-bar cf-quiz-sticky-bottom"
                        data-font={element.options["quiz-button-font"]}
                      >
                        <div className="cf-quiz-sticky-buttons-inner">
                          {quizBackSticky && backButtonJSX}
                          {quizNextSticky && nextButtonJSX}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {currentQuestionIndex() == -1 && props.builder == true && (
            <div className="fallback-empty-example">
              {props.device == "desktop" && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <img src={QuizExampleDesktop} />
                </div>
              )}

              {props.device == "mobile" && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <img src={QuizExampleMobile} />
                </div>
              )}
            </div>
          )}

          <div
            id="cf-quiz-confirmation-type"
            style={{
              display:
                currentQuestionIndex() == -1 && props.builder !== true
                  ? "block"
                  : "none",
            }}
          >
            {element.options["confirmation-type"] == "message" && (
              <p>{element.options["confirmation"] || "Thanks!"}</p>
            )}
          </div>

          <BuilderStyleTag
            builder={props.builder}
            styleKey={`cta:${props.cta.id}:element:${element.id}:quiz`}
            stylesheet={stylesheet}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default React.memo(ElementQuiz, arePropsEqual);
